import {useEffect, useRef, useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Link} from 'react-router-dom'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Switch} from '@/components/ui/switch'
import {PorQue} from '@/components/ui/por-que'
import {Tag} from '@/components/ui/tag'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {CoverageLine} from '@/components/coverage-line'
import {DescargarEvidencia, MiniaturaDeEvidencia} from '@/components/descargar-evidencia'
import {DesignationsBlock} from '@/components/designations-block'
import {EmployeePicker} from '@/components/employee-picker'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {mesLargo} from '@/components/rejilla-mensual'
import {SoportesEsperadosBlock} from '@/components/soportes-esperados-block'
import {RequestSoporteDialog, SolicitudesList} from '@/components/solicitudes-block'
import {ValidationState} from '@/components/validation-state'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import type {Employee} from '@/hooks/empresa/identidad/use-employees'
import type {Solicitud} from '@/hooks/sgsst/evidencias/use-solicitudes'
import type {SgsstBackedByKind, SgsstItem, SoporteCasilla} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {SinEventosBlock} from '@/components/sin-eventos-block'
import {
  EVIDENCE_ACCEPT,
  EVIDENCE_ALLOWED_TYPES,
  EVIDENCE_MAX_BYTES,
  evidenceTooBig,
  addMonthsIso,
  evidenceKindLabel,
  expiryState,
  formatSize,
  isAiAnalyzable,
  mintUploadTarget,
  rutaDeDescarga,
  uploadToStorage,
  useConfirmEvidence,
  useEditEvidenceSoporte,
  useRegisterEvidence,
  useRemoveEvidence,
  type Evidence,
} from '@/hooks/sgsst/evidencias/use-evidence'
import {track} from '@/lib/telemetry'
import {translateApiError} from '@/lib/errors'
import {formatDateOnly} from '@/lib/risk'
import {tieneCasillaSolicitable} from '@/lib/sgsst-readiness'
import {parsePickerValue, pickerOptions, pickerValue} from '@/lib/soporte-picker'
import {cn} from '@/lib/utils'

type Staged = {
  key: string
  file: File
  progress: number
  objectKey: string | null
  error: string | null
  selected: Employee[]
  validateAi: boolean
  soporteKey: string | null
  // Variante de la casilla (qué entidad certifica); 'file' es el documento simple.
  kind: string
  venceEl: string | null
  // Mes cubierto de una casilla `cadencia_mensual`; obligatorio antes de guardar.
  periodoMes: number | null
}

/** Una casilla `fuente=designacion` se cumple con una designación, nunca con un archivo. */
function fileCasillas(soportes: SoporteCasilla[]): SoporteCasilla[] {
  return soportes.filter((casilla) => casilla.fuente !== 'designacion')
}

/** Una casilla `cadencia_mensual` solo se elige desde su tarjeta de la rejilla, que fija casilla y
 * mes juntos: ofrecerla aquí pediría el mismo dato dos veces. */
function genericCasillas(casillas: SoporteCasilla[]): SoporteCasilla[] {
  return casillas.filter((casilla) => !casilla.cadencia_mensual)
}

function defaultVenceEl(casilla: SoporteCasilla | undefined): string | null {
  if (!casilla || casilla.vigencia_meses == null) return null
  return addMonthsIso(new Date(), casilla.vigencia_meses)
}

function missingPeriodo(row: Staged, uploadCasillas: SoporteCasilla[]): boolean {
  const casilla = uploadCasillas.find((c) => c.key === row.soporteKey)
  return !!casilla?.cadencia_mensual && row.periodoMes == null
}

// IPEVR respalda el sugerido con la matriz misma: su evidencia es un enlace, no un archivo
// (marking-spec §4.2).
const MODULE_EVIDENCE_LINK: Partial<Record<SgsstBackedByKind, {route: string; labelKey: string}>> = {
  ipevr: {route: 'matriz-riesgos', labelKey: 'evidence.moduleLink.ipevr'},
}

// Global a propósito: «Repetir anterior» vale entre estándares y tandas de toda la sesión.
let lastSelection: Employee[] = []

function FileBadge({contentType}: {contentType: string}) {
  const kind = evidenceKindLabel(contentType)
  return (
    <span
      className={cn(
        'flex h-14 w-full items-center justify-center rounded-t-[10px] font-mono text-[11px] font-semibold tracking-wider uppercase',
        kind === 'pdf' && 'bg-secondary text-destructive',
        (kind === 'xlsx' || kind === 'docx') && 'bg-secondary text-primary'
      )}>
      {kind}
    </span>
  )
}

export function EvidencePanel({
  businessId,
  standardCode,
  standardName,
  items,
  solicitudes,
  roster,
  editable,
  coverage,
}: {
  businessId: string
  standardCode: string
  standardName: string
  items: Evidence[]
  solicitudes: Solicitud[]
  roster: Employee[]
  editable: boolean
  coverage?: {item: SgsstItem; year: number; isClosed: boolean; workerCount: number}
}) {
  const {t} = useTranslation()
  const custodiaSalud = useCapabilities(businessId).sgsst_custodiar_salud
  const inputRef = useRef<HTMLInputElement>(null)
  const [staged, setStaged] = useState<Staged[]>([])
  const [removeError, setRemoveError] = useState<{id: string; message: string} | null>(null)
  const [removeTarget, setRemoveTarget] = useState<Evidence | null>(null)
  const [requestOpen, setRequestOpen] = useState(false)
  const [preselected, setPreselected] = useState<Employee[]>([])
  const [requestSoporteKey, setRequestSoporteKey] = useState<string | null>(null)
  const [editTarget, setEditTarget] = useState<Evidence | null>(null)
  // Preselección de la próxima subida; startUploads la consume una sola vez.
  const nextUploadRef = useRef<{employees: Employee[]; soporteKey: string | null; periodoMes: number | null} | null>(
    null
  )
  const register = useRegisterEvidence(businessId)
  const remove = useRemoveEvidence(businessId)
  const confirm = useConfirmEvidence(businessId)
  const soportes = coverage?.item.soportes ?? []
  const uploadCasillas = fileCasillas(soportes)
  // Sale del catálogo vía API (spec §5), nunca de un prefijo de código: el aviso de historia
  // clínica va solo donde se sube un concepto de aptitud.
  const conceptoAptitud = soportes.some((casilla) => casilla.concepto_aptitud)
  // Solo si alguna casilla la puede aportar una persona; si no, no hay a qué atar la solicitud.
  const solicitable = tieneCasillaSolicitable(soportes)
  // Lo pendiente de confirmar va primero, bajo su cabecera; `sort` es estable y el resto
  // conserva su orden.
  const ordenados = [...items].sort((a, b) => Number(!!b.pending_confirmation) - Number(!!a.pending_confirmation))
  const moduleLink =
    coverage?.item.sugerido && coverage.item.sugerido_backed_by_kind
      ? MODULE_EVIDENCE_LINK[coverage.item.sugerido_backed_by_kind]
      : undefined

  function openRequest(employee?: Employee, soporteKey?: string | null) {
    setPreselected(employee ? [employee] : [])
    setRequestSoporteKey(soporteKey ?? null)
    setRequestOpen(true)
  }

  function openUploadFor(employees: Employee[], soporteKey: string | null, periodoMes: number | null = null) {
    nextUploadRef.current = {employees, soporteKey, periodoMes}
    inputRef.current?.click()
  }

  function patchStaged(key: string, patch: Partial<Staged>) {
    setStaged((rows) => rows.map((row) => (row.key === key ? {...row, ...patch} : row)))
  }

  async function startUploads(files: FileList | null) {
    if (!files) return
    const preset = nextUploadRef.current
    nextUploadRef.current = null
    const pickable = genericCasillas(uploadCasillas)
    const defaultKey = preset?.soporteKey ?? (pickable.length === 1 ? pickable[0].key : null)
    const defaultCasilla = uploadCasillas.find((casilla) => casilla.key === defaultKey)
    const defaultPeriodoMes = preset?.periodoMes ?? null
    for (const file of Array.from(files)) {
      const key = `${file.name}-${Date.now()}-${Math.random()}`
      const early = !EVIDENCE_ALLOWED_TYPES.has(file.type)
        ? t('errors.evidenceBadFormat')
        : file.size > EVIDENCE_MAX_BYTES
          ? evidenceTooBig(t, file)
          : null
      setStaged((rows) => [
        ...rows,
        {
          key,
          file,
          progress: 0,
          objectKey: null,
          error: early,
          selected: preset?.employees ?? [],
          validateAi: isAiAnalyzable(file.type),
          soporteKey: defaultKey,
          kind: 'file',
          venceEl: defaultVenceEl(defaultCasilla),
          periodoMes: defaultPeriodoMes,
        },
      ])
      if (early) continue
      try {
        const target = await mintUploadTarget(businessId, file)
        await uploadToStorage(target, file, (pct) => patchStaged(key, {progress: pct}))
        patchStaged(key, {objectKey: target.object_key, progress: 100})
      } catch (err) {
        patchStaged(key, {error: translateApiError(err)})
      }
    }
    if (inputRef.current) inputRef.current.value = ''
  }

  function save(row: Staged) {
    register.mutate(
      {
        standard_code: standardCode,
        object_key: row.objectKey!,
        filename: row.file.name,
        content_type: row.file.type,
        size_bytes: row.file.size,
        employee_ids: row.selected.map((e) => e.id),
        validate_ai: row.validateAi,
        soporte_key: row.soporteKey,
        kind: row.kind,
        vence_el: row.venceEl,
        periodo_mes: row.periodoMes,
      },
      {
        onSuccess: () => {
          lastSelection = row.selected
          setStaged((rows) => rows.filter((r) => r.key !== row.key))
        },
        onError: (err) => patchStaged(row.key, {error: translateApiError(err)}),
      }
    )
  }

  function renovar(item: Evidence) {
    const casilla = soportes.find((s) => s.key === item.soporte_key)
    const roster_ids = new Set(item.employees.map((e) => e.id))
    const people = roster.filter((employee) => roster_ids.has(employee.id))
    if (casilla && casilla.fuente === 'empleado') openRequest(people[0], casilla.key)
    else openUploadFor(people, item.soporte_key)
  }

  return (
    <div className="mt-2 space-y-3 border-t border-border pt-3">
      {conceptoAptitud && <p className="tone-amber rounded-lg px-3 py-2 text-xs">{t('evidence.medicalHint')}</p>}
      {coverage && soportes.some((casilla) => casilla.admite_sin_eventos) && (
        <SinEventosBlock
          businessId={businessId}
          item={coverage.item}
          year={coverage.year}
          editable={editable && !coverage.isClosed}
        />
      )}
      <SoportesEsperadosBlock
        standardCode={standardCode}
        businessId={businessId}
        soportes={soportes}
        items={items}
        editable={editable}
        onUploadMes={(soporteKey, mes) => openUploadFor([], soporteKey, mes)}
      />
      {moduleLink && (
        <Link
          to={`/businesses/${businessId}/sgsst/${moduleLink.route}`}
          className="flex items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-xs text-foreground hover:border-primary hover:text-primary"
          data-testid="sugerido-evidence-link">
          {t(moduleLink.labelKey)}
        </Link>
      )}
      {coverage && coverage.item.person_scoped && (
        <CoverageLine
          businessId={businessId}
          standardCode={standardCode}
          items={items}
          roster={roster}
          year={coverage.year}
          isClosed={coverage.isClosed}
          workerCount={coverage.workerCount}
          solicitudes={editable ? solicitudes : []}
          onRequest={
            editable
              ? (employee) => openRequest(employee, soportes.find((casilla) => casilla.person_scoped)?.key)
              : undefined
          }
          personCasilla={soportes.find((casilla) => casilla.person_scoped)}
          onUploadFor={editable ? (employee, soporteKey) => openUploadFor([employee], soporteKey) : undefined}
          companyCasillas={soportes.filter((casilla) => !casilla.person_scoped && casilla.fuente !== 'designacion')}
        />
      )}
      <DesignationsBlock businessId={businessId} standardCode={standardCode} roster={roster} editable={editable} />
      {editable && (
        <>
          <input
            ref={inputRef}
            type="file"
            accept={EVIDENCE_ACCEPT}
            multiple
            hidden
            data-testid={`evidence-input-${standardCode}`}
            onChange={(e) => startUploads(e.target.files)}
          />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()}>
              {t('evidence.upload')}
            </Button>
            {solicitable && (
              <Button size="sm" data-testid={`request-soporte-${standardCode}`} onClick={() => openRequest()}>
                {t('solicitudes.requestSoporte')}
              </Button>
            )}
          </div>
        </>
      )}
      <SolicitudesList
        businessId={businessId}
        solicitudes={solicitudes}
        personScoped={coverage?.item.person_scoped ?? false}
        editable={editable}
      />
      <RequestSoporteDialog
        businessId={businessId}
        standardCode={standardCode}
        standardName={standardName}
        personScoped={coverage?.item.person_scoped ?? false}
        roster={roster}
        open={requestOpen}
        onOpenChange={setRequestOpen}
        preselected={preselected}
        soporteCasilla={soportes.find((casilla) => casilla.key === requestSoporteKey)}
      />

      {staged.map((row) => {
        const cadenciaCasilla = uploadCasillas.find((c) => c.key === row.soporteKey && c.cadencia_mensual)
        return (
          <div key={row.key} className="space-y-2 rounded-lg border border-border p-3">
            <div className="flex items-center gap-3">
              <span className="max-w-56 truncate text-sm font-medium">{row.file.name}</span>
              {row.error ? (
                <span className="text-xs text-destructive">{row.error}</span>
              ) : row.objectKey ? (
                <span className="font-mono text-xs text-primary">✓</span>
              ) : (
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-secondary">
                  <div className="h-full rounded-full bg-primary transition-all" style={{width: `${row.progress}%`}} />
                </div>
              )}
              <div className="ml-auto flex gap-1.5">
                <Button
                  size="sm"
                  disabled={!row.objectKey || register.isPending || missingPeriodo(row, uploadCasillas)}
                  onClick={() => save(row)}>
                  {t('common.save')}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setStaged((rows) => rows.filter((r) => r.key !== row.key))}>
                  {t('evidence.discard')}
                </Button>
              </div>
            </div>
            {cadenciaCasilla ? (
              <p
                className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase"
                data-testid="soporte-periodo-resuelto">
                {t('evidence.soportes.periodoResuelto', {
                  titulo: cadenciaCasilla.titulo,
                  mes: mesLargo(row.periodoMes!),
                  year: coverage?.year ?? '',
                })}
              </p>
            ) : (
              uploadCasillas.length > 0 && (
                <div className="flex flex-wrap items-end gap-3">
                  <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                    {t('evidence.soportes.pickerLabel')}
                    <select
                      value={pickerValue(row.soporteKey, row.kind)}
                      data-testid="soporte-picker"
                      className="h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground"
                      onChange={(e) => {
                        const {soporteKey, kind} = parsePickerValue(e.target.value)
                        const casilla = uploadCasillas.find((c) => c.key === soporteKey)
                        patchStaged(row.key, {soporteKey, kind, venceEl: defaultVenceEl(casilla), periodoMes: null})
                      }}>
                      <option value="">{t('evidence.soportes.pickerUnset')}</option>
                      {pickerOptions(genericCasillas(uploadCasillas)).map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  {row.soporteKey != null &&
                    uploadCasillas.find((c) => c.key === row.soporteKey)?.vigencia_meses != null && (
                      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                        {t('evidence.soportes.venceLabel')}
                        <Input
                          type="date"
                          value={row.venceEl ?? ''}
                          data-testid="soporte-vence-input"
                          className="h-8 max-w-40 font-mono text-xs"
                          onChange={(e) => patchStaged(row.key, {venceEl: e.target.value || null})}
                        />
                      </label>
                    )}
                </div>
              )
            )}
            {isAiAnalyzable(row.file.type) ? (
              <label className="flex w-fit items-center gap-2 text-xs text-muted-foreground">
                <Switch
                  checked={row.validateAi}
                  onCheckedChange={(v) => {
                    patchStaged(row.key, {validateAi: v})
                    track('ai_toggle_used', {business_id: businessId, standard_code: standardCode, enabled: v})
                  }}
                  data-testid="ai-toggle"
                  aria-label={t('validation.analyze')}
                />
                {t('validation.analyze')}
              </label>
            ) : (
              <div className="flex w-fit items-center gap-2 text-xs text-muted-foreground">
                <Switch checked={false} disabled data-testid="ai-toggle" aria-label={t('validation.analyze')} />
                <span data-testid="ai-skip-hint">{t('validation.skipHint')}</span>
              </div>
            )}
            <EmployeePicker
              roster={roster}
              selected={row.selected}
              onChange={(selected) => patchStaged(row.key, {selected})}
              lastSelection={lastSelection}
            />
          </div>
        )
      })}

      {items.length === 0 && staged.length === 0 && !moduleLink && (
        <p className="text-xs text-muted-foreground">{t('evidence.empty')}</p>
      )}

      {/* La procedencia va una vez, en la cabecera del grupo. Su nombre accesible no puede ser
          «Confirmar vigencia»: los specs buscan ese texto por rol dentro de la fila. */}
      {items.some((item) => item.pending_confirmation) && (
        <div className="flex flex-wrap items-center gap-2" data-testid="evidence-pending-confirmation-grupo">
          <Tag tone="amber">{t('evidence.pendingConfirmation')}</Tag>
          <PorQue
            label={t('evidence.pendingConfirmationPorqueLabel')}
            ariaLabel={t('evidence.pendingConfirmationPorqueAria')}
            titulo={t('evidence.pendingConfirmation')}
            testId="evidence-pending-confirmation-porque"
            contenidoTestId="evidence-pending-confirmation-porque-contenido">
            {t('evidence.pendingConfirmationPorque')}
          </PorQue>
        </div>
      )}

      {items.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {ordenados.map((item) => (
            <div key={item.id} className="overflow-hidden rounded-[11px] border border-border bg-card">
              {/* Una miniatura de un documento de salud sería abrirlo sin que nadie lo pida. */}
              {evidenceKindLabel(item.content_type) === 'img' && !item.de_salud ? (
                <MiniaturaDeEvidencia ruta={rutaDeDescarga(businessId, item.id)} alt={item.filename} />
              ) : (
                <FileBadge contentType={item.content_type} />
              )}
              <div className="space-y-1.5 p-2.5">
                <DescargarEvidencia
                  ruta={rutaDeDescarga(businessId, item.id)}
                  reservado={item.de_salud && !custodiaSalud}
                  className="block w-full truncate text-xs font-semibold text-foreground hover:text-primary"
                  title={item.filename}>
                  {item.filename}
                </DescargarEvidencia>
                <p className="font-mono text-[10px] text-muted-foreground">{formatSize(item.size_bytes)}</p>
                {item.employees.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {item.employees.slice(0, 2).map((employee) => (
                      <span
                        key={employee.id}
                        className="max-w-full truncate rounded-full bg-accent px-2 py-0.5 text-[10px] font-semibold text-accent-foreground">
                        {employee.name} {employee.last_name.charAt(0)}.
                      </span>
                    ))}
                    {item.employees.length > 2 && (
                      <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-semibold text-accent-foreground">
                        +{item.employees.length - 2}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                    {t('evidence.businessLevel')}
                  </span>
                )}
                {item.aportado && (
                  <Tag tone="green" className="w-fit" data-testid="aportado-tag">
                    {t('solicitudes.aportadoTag')}
                  </Tag>
                )}
                {item.soporte_titulo && (
                  <Tag tone="neutral" className="w-fit max-w-full truncate" data-testid="soporte-chip">
                    {item.variante_titulo ? `${item.soporte_titulo} · ${item.variante_titulo}` : item.soporte_titulo}
                  </Tag>
                )}
                <VencimientoBadge venceEl={item.vence_el} />
                {item.pending_confirmation && (
                  <p
                    className="tone-amber rounded px-1.5 py-0.5 font-mono text-[9px] tracking-wider uppercase"
                    data-testid="evidence-pending-chip">
                    {t('evidence.pendingConfirmation')}
                    {item.vence_el && <> · {formatDateOnly(item.vence_el)}</>}
                  </p>
                )}
                <ValidationState item={item} businessId={businessId} editable={editable} />
                {editable && (
                  <div className="flex flex-wrap gap-1 pt-0.5">
                    {item.pending_confirmation && (
                      <Button size="sm" className="h-6 px-2 text-[11px]" onClick={() => confirm.mutate(item.id)}>
                        {t('evidence.confirm')}
                      </Button>
                    )}
                    {expiryState(item.vence_el) === 'vencido' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 px-2 text-[11px]"
                        data-testid="renovar-soporte"
                        onClick={() => renovar(item)}>
                        {t('evidence.soportes.renovar')}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-[11px]"
                      data-testid="edit-soporte"
                      onClick={() => setEditTarget(item)}>
                      {t('evidence.soportes.edit')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-[11px]"
                      onClick={() => {
                        setRemoveError(null)
                        setRemoveTarget(item)
                      }}>
                      {t('evidence.remove')}
                    </Button>
                  </div>
                )}
                {removeError?.id === item.id && (
                  <p className="text-xs text-destructive" data-testid="evidence-remove-error">
                    {removeError.message}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <ConfirmDialog
        open={!!removeTarget}
        onOpenChange={(open) => !open && setRemoveTarget(null)}
        title={t('evidence.removeTitle')}
        description={t('evidence.removeBody', {filename: removeTarget?.filename ?? ''})}
        confirmLabel={t('evidence.remove')}
        cancelLabel={t('common.cancel')}
        pending={remove.isPending}
        onConfirm={() => {
          if (!removeTarget) return
          const target = removeTarget
          remove.mutate(target.id, {
            onSuccess: () => setRemoveTarget(null),
            // Un bloqueo de carta o soporte responde 409: se muestra junto al archivo.
            onError: (err) => {
              setRemoveTarget(null)
              setRemoveError({id: target.id, message: translateApiError(err)})
            },
          })
        }}
      />
      <EditSoporteDialog
        businessId={businessId}
        target={editTarget}
        casillas={uploadCasillas}
        onOpenChange={(open) => !open && setEditTarget(null)}
      />
    </div>
  )
}

/** Ámbar dentro de la ventana de 30 días que usa el backend, rojo al vencer. */
function VencimientoBadge({venceEl}: {venceEl: string | null | undefined}) {
  const {t} = useTranslation()
  const state = expiryState(venceEl)
  if (!venceEl || !state) return null
  return (
    <Tag tone={state === 'vencido' ? 'red' : 'amber'} className="w-fit" data-testid="vencimiento-badge">
      {t(state === 'vencido' ? 'evidence.soportes.vencioEl' : 'evidence.soportes.venceEl', {
        date: formatDateOnly(venceEl),
      })}
    </Tag>
  )
}

/** Los vínculos con personas no se editan: solo casilla, vencimiento o mes cubierto. */
function EditSoporteDialog({
  businessId,
  target,
  casillas,
  onOpenChange,
}: {
  businessId: string
  target: Evidence | null
  casillas: SoporteCasilla[]
  onOpenChange: (open: boolean) => void
}) {
  const {t} = useTranslation()
  const edit = useEditEvidenceSoporte(businessId)
  const [soporteKey, setSoporteKey] = useState<string | null>(null)
  const [kind, setKind] = useState<string>('file')
  const [venceEl, setVenceEl] = useState<string | null>(null)
  const [periodoMes, setPeriodoMes] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const selectedCasilla = casillas.find((c) => c.key === soporteKey)

  useEffect(() => {
    if (target) {
      setSoporteKey(target.soporte_key ?? null)
      setKind(target.kind)
      setVenceEl(target.vence_el ?? null)
      setPeriodoMes(target.periodo_mes ?? null)
      setError(null)
    }
  }, [target])

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!target) return
    edit.mutate(
      {evidenceId: target.id, soporte_key: soporteKey, kind, vence_el: venceEl, periodo_mes: periodoMes},
      {
        onSuccess: () => onOpenChange(false),
        onError: (err) => setError(translateApiError(err)),
      }
    )
  }

  return (
    <Dialog open={!!target} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('evidence.soportes.editTitle')}</DialogTitle>
        </DialogHeader>
        <p className="-mt-2 truncate text-sm text-muted-foreground">{target?.filename}</p>
        <form onSubmit={submit} className="space-y-4" data-testid="edit-soporte-form">
          {casillas.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="edit_soporte_key">{t('evidence.soportes.pickerLabel')}</Label>
              <select
                id="edit_soporte_key"
                value={pickerValue(soporteKey, kind)}
                className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground"
                onChange={(e) => {
                  const parsed = parsePickerValue(e.target.value)
                  setSoporteKey(parsed.soporteKey)
                  setKind(parsed.kind)
                }}>
                <option value="">{t('evidence.soportes.pickerUnset')}</option>
                {pickerOptions(casillas).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          {selectedCasilla?.cadencia_mensual ? (
            <div className="space-y-2">
              <Label htmlFor="edit_periodo_mes">{t('evidence.soportes.periodoLabel')}</Label>
              <select
                id="edit_periodo_mes"
                value={periodoMes ?? ''}
                data-testid="edit-periodo-mes"
                className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground"
                onChange={(e) => setPeriodoMes(e.target.value ? Number(e.target.value) : null)}>
                <option value="">{t('evidence.soportes.pickerUnset')}</option>
                {Array.from({length: 12}, (_, i) => i + 1).map((month) => (
                  <option key={month} value={month}>
                    {mesLargo(month)}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="edit_vence_el">{t('evidence.soportes.venceLabel')}</Label>
              <Input
                id="edit_vence_el"
                type="date"
                value={venceEl ?? ''}
                className="max-w-44 font-mono"
                onChange={(e) => setVenceEl(e.target.value || null)}
              />
            </div>
          )}
          <FormDialogFooter
            error={error}
            pending={edit.isPending}
            submitLabel={t('common.save')}
            onCancel={() => onOpenChange(false)}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}
